import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Seo, BareLayout } from "@/components/Layout";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const CANTONS = ["Zúrich", "Berna", "Lucerna", "San Galo", "Basilea", "Aargau", "Turgovia", "Otro / aún no lo sé"];
const SECTORS = ["Hostelería", "Limpieza", "Construcción", "Logística", "Restauración", "Cuidados", "Comercio", "Oficinas", "Otro"];
const GOALS = ["Vivir el día a día", "Encontrar trabajo", "Mejorar en mi trabajo actual", "Integrarme socialmente", "Prepararme para un examen"];
const STYLES = ["Lecciones cortas", "Mucha práctica", "Conversación", "Vocabulario primero"];
const LEVELS = ["Principiante (desde cero)", "A1", "A2", "B1"];
const Field = ({ label, children }) => (<div><label className="block text-sm font-medium mb-1.5">{label}</label>{children}</div>);

export function Register() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", level: LEVELS[0], canton: CANTONS[0], sector: SECTORS[0], goal: GOALS[0], minutes_per_day: 15, style: STYLES[0], target_date: "" });
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ name: f.name, email: f.email, password: f.password,
        profile: { level: f.level, canton: f.canton, sector: f.sector, goal: f.goal, minutes_per_day: Number(f.minutes_per_day), style: f.style, target_date: f.target_date, plan: "free" } });
      toast.success("¡Cuenta creada! Bienvenido/a a Hallo Suiza.");
      nav("/panel");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const input = "w-full h-11 rounded-md border border-input bg-background px-3 text-sm";

  return (
    <BareLayout>
      <Seo title="Crear cuenta" description="Regístrate gratis en Hallo Suiza." noindex />
      <div className="min-h-screen grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-12">
          <Logo />
          <div>
            <h2 className="font-display font-black text-4xl leading-tight">Empieza gratis hoy.</h2>
            <p className="mt-4 text-primary-foreground/90 max-w-sm">Personalizamos tu ruta según tu nivel, tu cantón y tu objetivo. Sin datos innecesarios.</p>
          </div>
          <p className="text-sm text-primary-foreground/70">Solo pedimos lo necesario para adaptar tu aprendizaje.</p>
        </div>
        <div className="flex items-center justify-center p-6 sm:p-12">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
            <div className="lg:hidden mb-8"><Logo /></div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Paso {step} de 2</p>
            <h1 className="font-display font-bold text-2xl mb-6">{step === 1 ? "Crea tu cuenta" : "Personaliza tu ruta"}</h1>
            {step === 1 ? (
              <div className="space-y-4">
                <Field label="Nombre o alias"><input required className={input} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="reg-name" /></Field>
                <Field label="Correo electrónico"><input required type="email" className={input} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" /></Field>
                <Field label="Contraseña (mín. 6)"><input required type="password" minLength={6} className={input} value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" /></Field>
                <Button type="button" className="w-full rounded-full" onClick={() => { if (f.name && f.email && f.password.length >= 6) setStep(2); else toast.error("Completa los campos."); }} data-testid="reg-next">Continuar</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Field label="Nivel aproximado"><select className={input} value={f.level} onChange={(e) => set("level", e.target.value)} data-testid="reg-level">{LEVELS.map((x) => <option key={x}>{x}</option>)}</select></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Cantón"><select className={input} value={f.canton} onChange={(e) => set("canton", e.target.value)}>{CANTONS.map((x) => <option key={x}>{x}</option>)}</select></Field>
                  <Field label="Sector"><select className={input} value={f.sector} onChange={(e) => set("sector", e.target.value)}>{SECTORS.map((x) => <option key={x}>{x}</option>)}</select></Field>
                </div>
                <Field label="Objetivo principal"><select className={input} value={f.goal} onChange={(e) => set("goal", e.target.value)}>{GOALS.map((x) => <option key={x}>{x}</option>)}</select></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Minutos al día"><input type="number" min={5} max={120} className={input} value={f.minutes_per_day} onChange={(e) => set("minutes_per_day", e.target.value)} /></Field>
                  <Field label="Preferencia"><select className={input} value={f.style} onChange={(e) => set("style", e.target.value)}>{STYLES.map((x) => <option key={x}>{x}</option>)}</select></Field>
                </div>
                <Field label="Fecha objetivo (opcional)"><input type="date" className={input} value={f.target_date} onChange={(e) => set("target_date", e.target.value)} /></Field>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" className="rounded-full" onClick={() => setStep(1)}>Atrás</Button>
                  <Button type="submit" className="flex-1 rounded-full" disabled={loading} data-testid="reg-submit">{loading ? <Loader2 className="animate-spin" size={16} /> : "Crear cuenta gratis"}</Button>
                </div>
              </div>
            )}
            <p className="mt-6 text-sm text-muted-foreground">¿Ya tienes cuenta? <Link to="/iniciar-sesion" className="text-primary underline">Inicia sesión</Link></p>
          </form>
        </div>
      </div>
    </BareLayout>
  );
}

export function Login() {
  const { login, formatApiError } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); toast.success("¡Bienvenido/a de nuevo!"); nav("/panel"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  const input = "w-full h-11 rounded-md border border-input bg-background px-3 text-sm";
  return (
    <BareLayout>
      <Seo title="Iniciar sesión" noindex />
      <div className="min-h-screen flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8"><Logo /></div>
          <h1 className="font-display font-bold text-2xl mb-6">Inicia sesión</h1>
          <div className="space-y-4">
            <div><label className="block text-sm font-medium mb-1.5">Correo</label><input required type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" /></div>
            <div><label className="block text-sm font-medium mb-1.5">Contraseña</label><input required type="password" className={input} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" /></div>
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit">{loading ? <Loader2 className="animate-spin" size={16} /> : "Entrar"}</Button>
          </div>
          <p className="mt-6 text-sm text-muted-foreground">¿No tienes cuenta? <Link to="/registro" className="text-primary underline">Regístrate gratis</Link></p>
        </form>
      </div>
    </BareLayout>
  );
}
